import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Ticket, ChevronLeft, Clock, User, Calendar, Tag,
  MessageSquare, Send, Loader2, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Waiting': 'bg-orange-100 text-orange-700',
  'Resolved': 'bg-green-100 text-green-700',
  'Closed': 'bg-gray-100 text-gray-600',
};

const PRIORITY_COLORS = {
  'Critical': 'bg-red-100 text-red-700',
  'High': 'bg-orange-100 text-orange-700',
  'Medium': 'bg-yellow-100 text-yellow-700',
  'Low': 'bg-slate-100 text-slate-600',
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return d; }
};

const PortalTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('portal_token');
  const portalUser = JSON.parse(localStorage.getItem('portal_user') || '{}');

  useEffect(() => {
    if (!token) { navigate('/portal/login'); return; }
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const res = await api.portalGetTicket(ticketId);
      setTicket(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/portal/login');
      } else if (err.response?.status === 403) {
        toast.error('You do not have access to this ticket');
        navigate('/portal');
      } else {
        toast.error('Failed to load ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.portalReply(ticketId, reply);
      toast.success('Reply sent');
      setReply('');
      fetchTicket();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Ticket not found</p>
      </div>
    );
  }

  const isClosed = ['Closed', 'Resolved'].includes(ticket.status);
  const notes = ticket.notes || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/portal')}
              className="gap-1 text-slate-600"
              data-testid="portal-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              My Tickets
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{portalUser.full_name || portalUser.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Ticket Header */}
        <Card className="border-slate-200" data-testid="portal-ticket-header">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="text-xs font-medium text-slate-400">#{ticket.ticket_number}</span>
                <h2 className="text-lg font-bold text-slate-900 mt-0.5">{ticket.summary}</h2>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`${PRIORITY_COLORS[ticket.priority] || ''}`}>{ticket.priority}</Badge>
                <Badge className={`${STATUS_COLORS[ticket.status] || ''}`}>{ticket.status}</Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Created: {formatDate(ticket.created_on)}</span>
              {ticket.assigned_to && (
                <span className="flex items-center gap-1"><User className="h-3 w-3" />Assigned: {ticket.assigned_to}</span>
              )}
              {ticket.category && (
                <span className="flex items-center gap-1"><Tag className="h-3 w-3" />{ticket.category}</span>
              )}
            </div>
            {ticket.description && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">
                {ticket.description}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Communication Thread */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-slate-500" />
              Communication ({notes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {notes.length === 0 ? (
              <p className="text-xs text-slate-400 py-4 text-center">No messages yet. Add a reply below.</p>
            ) : (
              notes.map((note, i) => {
                const isRequester = note.author_type === 'requester';
                return (
                  <div
                    key={note.note_id || i}
                    className={`p-3 rounded-lg text-sm ${
                      isRequester
                        ? 'bg-blue-50 border border-blue-100 ml-8'
                        : 'bg-white border border-slate-200 mr-8'
                    }`}
                    data-testid={`portal-note-${note.note_id || i}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${isRequester ? 'text-blue-700' : 'text-slate-600'}`}>
                        {note.author || 'IT Support'}
                      </span>
                      <span className="text-xs text-slate-400">{formatDate(note.created_at)}</span>
                    </div>
                    <div className="text-slate-700 whitespace-pre-wrap">{note.content}</div>
                  </div>
                );
              })
            )}

            {/* Reply box */}
            {!isClosed ? (
              <div className="pt-3 border-t border-slate-100">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply..."
                  rows={3}
                  className="mb-2"
                  data-testid="portal-reply-input"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleReply}
                    disabled={sending || !reply.trim()}
                    size="sm"
                    className="gap-2 bg-slate-900 hover:bg-slate-800"
                    data-testid="portal-send-reply-btn"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="pt-3 border-t border-slate-100 text-center text-xs text-slate-400 py-2">
                This ticket is {ticket.status.toLowerCase()}. Contact IT to reopen.
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PortalTicketDetail;
